import React, { FC, useEffect, useRef, useState } from "react"
import { graphql } from "gatsby"

import { TabsBlockFragmentFragment } from "@graphqlTypes"

import { isClientSide } from "@utils"

import Tabs from "@components/Tabs/Tabs"

import LpSection from "../LpSection/LpSection"
import TextBlock from "../TextBlock/TextBlock"
import MediaBlock from "../MediaBlock/MediaBlock"
import ContentPlusMediaBlock from "../ContentPlusMediaBlock/ContentPlusMediaBlock"

import "./TabsBlock.scss"

const TABBED_ITEMS_CONTENT_COMPONENTS: { [k: string]: FC<any> } = {
  ContentfulLpTextBlock: TextBlock,
  ContentfulLpMediaBlock: MediaBlock,
  ContentfulLpContentPlusMediaBlock: ContentPlusMediaBlock,
}

const TabsBlock: FC<TabsBlockFragmentFragment> = ({
  background,
  contentful_id,
  description,
  includeBackground,
  marginBottom,
  tabItem,
  title,
  useDarkMode,
  useSpaceEvenly,
}) => {
  const SlideWrapperRef = useRef<HTMLDivElement | null>(null)
  const [activeSlide, setActiveSlide] = useState<number>(0)
  const [slideHeights, setSlideHeights] = useState<number[]>([])

  useEffect(() => {
    if (!isClientSide() || !SlideWrapperRef.current) return

    const calculateSlideHeights = () => {
      if (!SlideWrapperRef.current) return

      const slideEls = Array.from(SlideWrapperRef.current.childNodes).map(
        (node) => node as HTMLDivElement
      )
      const _slideHeights = slideEls.map(
        (slideEl) => slideEl.getBoundingClientRect().height
      )

      setSlideHeights(_slideHeights)
    }

    window.addEventListener("resize", calculateSlideHeights)

    calculateSlideHeights()

    return () => {
      window.removeEventListener("resize", calculateSlideHeights)
    }
  }, [])

  return tabItem ? (
    <LpSection
      background={background}
      className="TabsBlock"
      includeBackground={includeBackground}
      marginBottom={marginBottom}
      text={description}
      title={title}
      uniqueId={contentful_id}
      useDarkMode={useDarkMode}
    >
      <div className="container">
        <div className="column--1-12-L">
          <Tabs
            activeOption={activeSlide}
            options={tabItem.map((slide, i) => (slide ? {
              title: slide.titleDesktop || "",
              mobileTitle: slide.titleMobile || "",
              value: i,
            } : null)).filter(s => !!s)}
            onClickOption={(i) => setActiveSlide(i as number || 0)}
            useSpaceEvenly={useSpaceEvenly}
          />
          <div
            className="TabsBlock--SlidesWrapper"
            ref={SlideWrapperRef}
            style={{
              height: slideHeights[activeSlide],
            }}
          >
            {tabItem.map((slide, i) => {
              if (!slide) return null

              const TabInnerComponent = slide.itemContent?.__typename
                ? TABBED_ITEMS_CONTENT_COMPONENTS[
                slide.itemContent.__typename
                ]
                : null

              return TabInnerComponent ? (
                <div
                  key={slide.titleDesktop || ""}
                  className={`TabsBlock--Slide${i === activeSlide ? " is-active" : ""}`}
                >
                  <TabInnerComponent
                    {...slide.itemContent}
                    key={slide.titleMobile}
                  />
                </div>
              ) : null
            })}
          </div>
        </div>
      </div>
    </LpSection>
  ) : null
}

export const TabsBlockFragment = graphql`
  fragment TabItemFragment on ContentfulLpTabItem {
    titleDesktop
    titleMobile
    itemContent {
      __typename
      ... on ContentfulLpContentPlusMediaBlock {
        ...ContentPlusMediaBlockFragment
      }
      ... on ContentfulLpMediaBlock {
        ...MediaBlockFragment
      }
      ... on ContentfulLpTextBlock {
        ...TextBlockFragment
      }
    }
  }
  
  fragment TabsBlockFragment on ContentfulLpTabsBlock {
    contentful_id
    title
    description {
      raw
    }
    tabItem {
      ... on ContentfulLpTabItem {
        ...TabItemFragment
      }
    }
    useSpaceEvenly
    background
    includeBackground
    marginBottom
    useDarkMode
  }
`

export default TabsBlock
